// src/pages/UnsoldTicketWarehouseReport/components/ApprovedRecallWarehouseList.tsx
// Mục 8.f, README_UPDATE.md — danh sách "Phiếu đã thẩm định & phê duyệt" lấy dữ liệu THẬT từ
// TicketRecall (Status = Approved), thay cho ApprovedReconciliationList.tsx cũ đọc
// TicketReturnReconciliation (luồng chết, không còn dữ liệu). Cột "Thao tác" chỉ còn "Xem chi
// tiết" — việc gán vị trí kho chuyển vào bên trong modal chi tiết.

import React from 'react';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { AgentMap, getAgentCode, getAgentName } from 'helpers/agentLookup';
import { TicketRecall } from 'pages/TicketRecall/types';
import { TicketRecallPagination } from 'pages/TicketRecall/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_warehouse_report');

interface ApprovedRecallWarehouseListProps {
  items: TicketRecall[];
  pagination: TicketRecallPagination;
  agentMap: AgentMap;
  loading: boolean;
  onViewDetail: (item: TicketRecall) => void;
}

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

const ApprovedRecallWarehouseList: React.FC<ApprovedRecallWarehouseListProps> = ({
  items,
  pagination,
  agentMap,
  loading,
  onViewDetail,
}) => {
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplay(location.pathname, 'button-detail');
  const columnCount = canShowActionColumn ? 7 : 6;

  // Mục 11.c, README_UPDATE.md — dòng tổng cộng "Tổng vé thu hồi"/"Quy đổi tiền" cho TOÀN DANH SÁCH
  // đã duyệt (không phải chỉ trang hiện tại), nên chỉ cộng dồn được đúng khi đang xem 1 trang —
  // trang có phân trang thì tổng này chỉ phản ánh trang đang xem (ghi rõ trong tiêu đề dòng tổng).
  const pageTotalQuantity = items.reduce((sum, item) => sum + (item.totalQuantity || 0), 0);
  const pageTotalValue = items.reduce((sum, item) => sum + (item.estimatedValue || 0), 0);

  return (
    <DataTableShell
      headerRow={
        <tr>
          <th style={{ width: 60 }} className="text-center">{st('stt_column')}</th>
          <th>{st('ma_phieu_thu_hoi_column')}</th>
          <th>{st('dai_ly_column')}</th>
          <th className="text-center">{st('tong_ve_thu_hoi_column')}</th>
          <th className="text-center">{st('quy_doi_tien_column')}</th>
          <th className="text-center">{st('vi_tri_kho_luu_tru_column')}</th>
          {canShowActionColumn && (
            <th style={{ width: 130 }} className="text-center">
              {st('thao_tac_column')}
            </th>
          )}
        </tr>
      }
      footerRow={
        items.length > 0 ? (
          <tr>
            <td colSpan={3} className="text-center fw-semibold">
              {st('tong_cong_trang_hien_tai_text')}
            </td>
            <td className="text-center text-success fw-semibold">{formatNumber(pageTotalQuantity)}</td>
            <td className="text-center fw-semibold">{formatCurrency(pageTotalValue)}</td>
            <td colSpan={canShowActionColumn ? 2 : 1}></td>
          </tr>
        ) : undefined
      }
    >
      {loading ? (
        Array.from({ length: 5 }).map((_, row) => (
          <tr key={row}>
            {Array.from({ length: columnCount }).map((__, col) => (
              <td key={col}>
                <div className="placeholder-glow">
                  <span className="placeholder col-8"></span>
                </div>
              </td>
            ))}
          </tr>
        ))
      ) : items.length === 0 ? (
        <tr>
          <td colSpan={columnCount} className="text-center text-muted py-5">
            <div className="mb-2">
              <i className="ri-archive-line fs-1 text-muted"></i>
            </div>
            {st('chua_co_phieu_thu_hoi_nao_text')}
          </td>
        </tr>
      ) : (
        items.map((item, index) => (
          <tr key={item.id}>
            <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
            <td>
              <div className="fw-medium text-primary">{item.recallCode}</div>
              {item.appraisedAt && (
                <small className="text-muted">
                  {st('duyet_hint')} {moment(item.appraisedAt).format('DD/MM/YYYY HH:mm')}
                </small>
              )}
            </td>
            <td>
              {item.agentId ? (
                <>
                  <div className="fw-medium">{getAgentName(agentMap, item.agentId)}</div>
                  <small className="text-muted">{getAgentCode(agentMap, item.agentId)}</small>
                </>
              ) : (
                st('kho_tong_text')
              )}
            </td>
            <td className="text-center text-success fw-semibold">{formatNumber(item.totalQuantity)}</td>
            <td className="text-center">{formatCurrency(item.estimatedValue)}</td>
            <td className="text-center">
              {item.warehouseLocation ? (
                <span className="badge bg-success-subtle text-success">
                  <i className="ri-map-pin-line me-1"></i>
                  {item.warehouseLocation}
                </span>
              ) : (
                <span className="badge bg-warning-subtle text-warning">{st('chua_gan_text')}</span>
              )}
            </td>
            {canShowActionColumn && (
              <td className="text-center">
                <Button
                  color="info"
                  outline
                  size="sm"
                  className="btn-icon"
                  title={st('xem_chi_tiet_title')}
                  data-action-code="button-detail"
                  data-action-name="Xem chi tiết phiếu thu hồi"
                  onClick={() => onViewDetail(item)}
                >
                  <i className="ri-eye-line"></i>
                </Button>
              </td>
            )}
          </tr>
        ))
      )}
    </DataTableShell>
  );
};

export default ApprovedRecallWarehouseList;
